import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { formatCOP } from '@/lib/utils';
import { BarChart3 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import { PeriodFilter, usePeriod } from '@/components/PeriodFilter';

const short = v => (Math.abs(v) >= 1e9 ? `${(v / 1e9).toFixed(1)}MM` : Math.abs(v) >= 1e6 ? `${Math.round(v / 1e6)}M` : `${Math.round(v / 1e3)}K`);
const tipStyle = { background: '#0A1F1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, color: '#fff', fontSize: 12 };

const BROKER_BARS = [{ k: 'generated', l: 'Generado', c: '#A38560' }, { k: 'received', l: 'Recibido', c: '#6BCB77' }];
const SHARE_BARS = [
  { k: 'selecto_due', l: 'SELECTO · corresponde', c: '#C4A87C' }, { k: 'selecto_paid', l: 'SELECTO · pagado', c: '#8B6F47' },
  { k: 'deeplook_due', l: 'Deeplook · corresponde', c: '#C084FC' }, { k: 'deeplook_paid', l: 'Deeplook · pagado', c: '#7E22CE' },
];

const Chart = ({ data, bars, testId }) => (
  <div data-testid={testId} style={{ height: Math.max(260, data.length * 70) }}>
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} layout="vertical" margin={{ left: 10, right: 20 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" horizontal={false} />
        <XAxis type="number" tickFormatter={short} tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} />
        <YAxis type="category" dataKey="name" width={110} tick={{ fill: 'rgba(255,255,255,0.7)', fontSize: 11 }} />
        <Tooltip formatter={v => formatCOP(v)} contentStyle={tipStyle} cursor={{ fill: 'rgba(255,255,255,0.03)' }} />
        <Legend wrapperStyle={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }} />
        {bars.map(b => <Bar key={b.k} dataKey={b.k} name={b.l} fill={b.c} radius={[0, 4, 4, 0]} barSize={10} />)}
      </BarChart>
    </ResponsiveContainer>
  </div>
);

const Total = ({ label, due, paid, cls, testId, sub = 'que corresponde' }) => (
  <div data-testid={testId} className="p-3 lg:p-4 bg-[#16302B] rounded-xl border border-white/5">
    <p className="text-[10px] uppercase tracking-widest text-white/40">{label}</p>
    <p className={`font-outfit text-lg font-semibold tabular-nums mt-1 ${cls}`}>{formatCOP(paid)}</p>
    {due !== undefined && <p className="text-[11px] text-white/40 tabular-nums">de {formatCOP(due)} {sub}</p>}
  </div>
);

export const BrokerCommissionChart = () => {
  const { period, setPeriod, query } = usePeriod();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!query) return;
    api.get(`/dashboard/broker-commission-chart?${query}`).then(setData).catch(() => {});
  }, [query]);

  const t = data?.totals;
  return (
    <div data-testid="broker-chart-section" className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 lg:p-6 space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
        <div>
          <h2 className="font-outfit text-lg font-medium text-white flex items-center gap-2"><BarChart3 size={18} strokeWidth={1.5} className="text-[#A38560]" />Comisiones por broker</h2>
          <p className="text-xs text-white/40 mt-1">Lo que generó y recibió cada broker, y lo que aportó a SELECTO y Deeplook.</p>
        </div>
        <PeriodFilter period={period} setPeriod={setPeriod} prefix="broker-chart" />
      </div>
      {data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Total label="Generado por brokers" paid={t.generated} cls="text-[#A38560]" testId="broker-chart-total-generated" />
            <Total label="Recibido por brokers" due={t.generated} paid={t.received} sub="generados" cls="text-[#6BCB77]" testId="broker-chart-total-received" />
            <Total label="Entregado a SELECTO" due={t.selecto_due} paid={t.selecto_paid} cls="text-[#C4A87C]" testId="broker-chart-total-selecto" />
            <Total label="Entregado a Deeplook" due={t.deeplook_due} paid={t.deeplook_paid} cls="text-purple-400" testId="broker-chart-total-deeplook" />
          </div>
          {data.brokers.length === 0 ? (
            <p className="py-8 text-center text-white/30 text-sm">Sin datos en el periodo</p>
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <div><h3 className="text-sm font-medium text-white mb-2">Generado vs recibido</h3><Chart data={data.brokers} bars={BROKER_BARS} testId="broker-chart-earned" /></div>
              <div><h3 className="text-sm font-medium text-white mb-2">Aportes a SELECTO y Deeplook</h3><Chart data={data.brokers} bars={SHARE_BARS} testId="broker-chart-shares" /></div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
